import { graphql } from 'react-relay';

import { expVal } from '@confluence/feature-experiments';
import { useIsNewUser } from '@confluence/targeting/entry-points/useIsNewUser.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import type { useCfind5291TemplateTouchpointFunctionalOnboardingContextQuery$data } from './__generated__/useCfind5291TemplateTouchpointFunctionalOnboardingContextQuery.graphql.ts';

const FIRST_TWO_WEEKS_THRESHOLD = 14;

const FunctionalOnboardingContextQuery = graphql`
	query useCfind5291TemplateTouchpointFunctionalOnboardingContextQuery($tenantId: ID!) {
		growthUnifiedProfile_siteProfile(tenantId: $tenantId) {
			functionalOnboardingContext(productKey: CONFLUENCE) {
				functionallyOnboarded
			}
		}
	}
`;

export const useCfind5291TemplateTouchpoint = () => {
	const { cloudId } = useSessionData();

	const { isNewUser, loading: isNewUserLoading } = useIsNewUser({
		activeDaysThreshold: FIRST_TWO_WEEKS_THRESHOLD,
	});

	const {
		data: siteProfileData,
		loading: siteProfileLoading,
		error: siteProfileError,
	} = useQueryInterop<useCfind5291TemplateTouchpointFunctionalOnboardingContextQuery$data>(
		FunctionalOnboardingContextQuery,
		{
			variables: { tenantId: cloudId! },
			skip: !cloudId || !expVal('cfind-5291-template-touchpoint', 'isEnabled', false),
			client: getAGGClient(),
		},
	);

	const isFunctionallyOnboarded =
		siteProfileData?.growthUnifiedProfile_siteProfile?.functionalOnboardingContext
			?.functionallyOnboarded;

	const loading = isNewUserLoading || siteProfileLoading;

	// If still loading, don't enable yet
	if (loading) {
		return { isEnabled: false, loading: true };
	}

	// Exclude users not within their first 2 weeks after being onboarded
	if (!isNewUser) {
		return { isEnabled: false, loading: false };
	}

	// Exclude users who have been functionally onboarded
	if (isFunctionallyOnboarded && !siteProfileError) {
		return { isEnabled: false, loading: false };
	}

	return {
		isEnabled: expVal('cfind-5291-template-touchpoint', 'isEnabled', false),
		loading: false,
	};
};
